import { useState } from 'react';
import { AlertCircle, Settings } from 'react-feather';
import { API_BASE_URL } from '../../config/api';

/* The three policy switches on a linked directory.
 *
 * Each control writes on change rather than behind a Save button: they are
 * independent single-value settings, and a shared Save would make it ambiguous
 * which of the three a failure applied to. The optimistic value is rolled back
 * on error so the control never disagrees with the server.
 *
 * "Require Microsoft sign-in" is the one that can lock people out, so the
 * backend refuses it when no admin holds a password. That 409 arrives with a
 * sentence worth reading, so it is shown as-is rather than replaced.
 */

const ROLES = [
  { value: 'MEMBER', label: 'Member' },
  { value: 'ADMIN', label: 'Admin' }
];

const SsoSettingsCard = ({ tenant, canManage, authHeaders, onChanged }) => {
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  const patch = async (field, value) => {
    if (saving) return;
    setSaving(field);
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/api/sso/tenant`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ [field]: value })
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(
          data?.detail || data?.error?.message || 'Could not save that setting.'
        );
      }
      // Re-read rather than trusting the local guess: enforce_sso in
      // particular can be refused by a rule this page does not evaluate.
      await onChanged();
    } catch (err) {
      setError(err.message || 'Could not save that setting.');
    } finally {
      setSaving('');
    }
  };

  const disabled = !canManage || Boolean(saving);

  return (
    <section className="account-section-card account-card-grow">
      <div className="account-section-header">
        <span className="account-section-icon" aria-hidden="true">
          <Settings />
        </span>
        <div className="account-section-heading">
          <h2 className="account-section-title">Sign-in policy</h2>
          <p className="account-section-description">
            How new people from your directory are treated when they arrive.
          </p>
        </div>
      </div>

      <div className="account-section-body">
        {error && (
          <div className="account-banner error" role="alert">
            <AlertCircle aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="sso-setting-row">
          <div className="sso-setting-copy">
            <h3 className="account-callout-title" id="sso-autoprovision-label">
              Create accounts automatically
            </h3>
            <p className="account-callout-text">
              Anyone in your directory who signs in gets an account here right
              away. With this off, an admin approves each person below before
              they can get in.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={tenant.auto_provision}
            aria-labelledby="sso-autoprovision-label"
            onClick={() => patch('auto_provision', !tenant.auto_provision)}
            disabled={disabled}
            className={`toggle-button${tenant.auto_provision ? ' active' : ''}`}
          >
            <div className="toggle-slider" />
          </button>
        </div>

        <div className="sso-setting-row">
          <div className="sso-setting-copy">
            <label className="account-callout-title" htmlFor="sso-default-role">
              Role for new people
            </label>
            <p className="account-callout-text">
              What someone can do the first time they sign in. You can change
              any individual afterwards.
            </p>
          </div>
          <select
            id="sso-default-role"
            className="sso-setting-select"
            value={tenant.default_role || 'MEMBER'}
            onChange={(e) => patch('default_role', e.target.value)}
            disabled={disabled}
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        <div className="sso-setting-row">
          <div className="sso-setting-copy">
            <h3 className="account-callout-title" id="sso-enforce-label">
              Require Microsoft sign-in
            </h3>
            <p className="account-callout-text">
              Turn off passwords for this organization entirely. At least one
              admin must keep a password, so there is always a way back in if
              the directory connection ever fails.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={tenant.enforce_sso}
            aria-labelledby="sso-enforce-label"
            onClick={() => patch('enforce_sso', !tenant.enforce_sso)}
            disabled={disabled}
            className={`toggle-button${tenant.enforce_sso ? ' active' : ''}`}
          >
            <div className="toggle-slider" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default SsoSettingsCard;
